<template>
	<div class="chart-modal">
		<div class="d-flex align-items-center mb16" :class="isMobile ? 'justify-content-center chart-modal__header--mobile' : 'justify-content-between'">
			<Icon v-if="isMobile" name="arrow-left" class="c24 pointer chart-modal__back" @click="close()" />
			<div class="fsp20 lh28 neue">{{ content.title }}</div>
			<Icon v-if="!isMobile" name="close" class="c24 pointer" @click="close()" />
		</div>
		<div class="chart-modal__content" :class="{ 'p24 bg-white rounded12': !isMobile }">
			<!-- Header with value and delta -->
			<div class="d-flex justify-content-between align-items-start mb16">
				<div>
					<div class="fsp16 lh24 primary-grey d-flex align-items-center gap8 neue">
						{{ content.title }}
						<Popper v-if="content.hint" :hover="true" :maxWidth="220">
							<Icon name="info" size="c20" class="chart-modal__info" />
							<template #content>{{ content.hint }}</template>
						</Popper>
					</div>
					<div class="fsp16 lh24 neue-bold mt4">
						{{ content.value }}
						<span v-if="content.currency" class="fsp16 primary-grey neue-roman">{{ content.currency }}</span>
					</div>
				</div>
				<div class="d-flex flex-column align-items-end" style="gap: 6px">
					<div class="chart-modal__delta" :class="modalDeltaClass(content.deltaClass)">
						<span v-if="getDeltaArrow(content.delta)" class="chart-modal__arrow">{{ getDeltaArrow(content.delta) }}</span>
						{{ content.delta }}
					</div>
					<div class="fsp14 lh20 primary-grey neue-roman">{{ content.deltaPeriodText }}</div>
				</div>
			</div>

			<!-- Chart -->
			<div class="chart-modal__chart">
				<Bar
					v-if="content.chartType === 'bar' && content.chartKey === 'conversion' && conversionBarOptions"
					:id="`modal-bar-${content.chartKey}`"
					:options="conversionBarOptions"
					:data="content.chartData"
					:plugins="bar3DPlugin ? [bar3DPlugin] : []"
				/>
				<Bar
					v-else-if="content.chartType === 'bar' && barOptions"
					:id="`modal-bar-${content.chartKey}`"
					:options="barOptions"
					:data="content.chartData"
				/>
				<Line
					v-else-if="content.chartType === 'line' && lineOptions"
					:id="`modal-line-${content.chartKey}`"
					:options="lineOptions"
					:data="content.chartData"
				/>
			</div>

			<!-- Period dates under chart -->
			<div class="fsp12 lh16 primary-grey d-flex justify-content-between align-items-center mt8 mb16 neue-roman">
				<span>{{ content.dateFrom }}</span>
				<span>{{ content.dateTo }}</span>
			</div>

			<!-- Loading -->
			<div v-if="loading" class="chart-modal__loading d-flex align-items-center justify-content-center">
				<span class="fsp14 lh20 primary-grey neue">{{ $t('views.Merchant.DashboardInvoices.49') }}</span>
			</div>

			<!-- Invoices table: Desktop -->
			<div v-else-if="invoices.length > 0 && !isMobile" class="chart-modal__table">
				<!-- Table header (fixed, outside scroll) -->
				<table class="chart-modal__tbl chart-modal__tbl-head neue-roman" :class="{ 'chart-modal__tbl--with-status': isConversion }">
					<thead>
						<tr>
							<th class="chart-modal__col-id">{{ $t('views.Merchant.DashboardInvoices.22') }}</th>
							<th>{{ $t('views.Merchant.DashboardInvoices.23') }}</th>
							<th>{{ $t('views.Merchant.DashboardInvoices.25') }}</th>
							<th>{{ $t('views.Merchant.DashboardInvoices.26') }}</th>
							<th v-if="isConversion">{{ $t('views.Merchant.DashboardInvoices.28') }}</th>
						</tr>
					</thead>
				</table>
				<!-- Table body (scrollable) -->
				<div class="chart-modal__table-scroll">
					<table class="chart-modal__tbl neue-roman" :class="{ 'chart-modal__tbl--with-status': isConversion }">
						<tbody>
							<tr v-for="inv in invoices" :key="inv.code">
								<td class="chart-modal__col-id">{{ inv.code }}</td>
								<td class="text-nowrap">{{ formatInvDate(inv.dt_create) }}</td>
								<td class="text-truncate" style="max-width: 140px">{{ inv.shop_name || inv.shop }}</td>
								<td class="text-nowrap">{{ formatAmount(inv.amount) }} {{ inv.currency }}</td>
								<td v-if="isConversion">
									<span class="status neue" :class="statusClass(inv.status)">{{ transformStatusText(inv.status) }}</span>
								</td>
							</tr>
						</tbody>
					</table>
				</div>

				<!-- Footer: Export CSV -->
				<div class="chart-modal__download pointer d-flex align-items-center justify-content-center gap8 mt12" @click="downloadCSV">
					<Icon name="download-file" class="c20" />
					<span class="fsp14 lh20 neue">Export CSV</span>
				</div>
			</div>

			<!-- Invoices table: Mobile -->
			<div v-else-if="invoices.length > 0 && isMobile" class="chart-modal__table">
				<table class="chart-modal__tbl chart-modal__tbl-head chart-modal__tbl--mobile neue-roman">
					<thead>
						<tr>
							<th class="chart-modal__col-id">ID</th>
							<th>{{ $t('views.Merchant.DashboardInvoices.51') }}</th>
							<th v-if="isConversion">{{ $t('views.Merchant.DashboardInvoices.51') }}</th>
						</tr>
					</thead>
				</table>
				<div class="chart-modal__table-scroll">
					<table class="chart-modal__tbl chart-modal__tbl--mobile neue-roman">
						<tbody>
							<tr v-for="inv in invoices" :key="inv.code">
								<td class="chart-modal__col-id chart-modal__col-id--top">{{ inv.code }}</td>
								<td>
									<div class="fsp14 lh20">{{ formatInvDate(inv.dt_create) }}</div>
									<div class="fsp14 lh20 text-truncate">{{ inv.shop_name || inv.shop }}</div>
									<div class="fsp14 lh20">{{ formatAmount(inv.amount) }} {{ inv.currency }}</div>
								</td>
								<td v-if="isConversion" class="chart-modal__col-status">
									<span class="status neue" :class="statusClass(inv.status)">{{ transformStatusText(inv.status) }}</span>
								</td>
							</tr>
						</tbody>
					</table>
				</div>

				<div class="chart-modal__download pointer d-flex align-items-center justify-content-center gap8 mt12" @click="downloadCSV">
					<Icon name="download-file" class="c20" />
					<span class="fsp14 lh20 neue">Export CSV</span>
				</div>
			</div>

			<!-- Empty state -->
			<div v-else class="chart-modal__empty d-flex align-items-center justify-content-center">
				<span class="fsp14 lh20 primary-grey neue">{{ $t('views.Merchant.DashboardInvoices.50') }}</span>
			</div>
		</div>
	</div>
</template>

<script>
import Icon from '@/components/icons/Icon';
import Popper from '@/components/service/Popper';
import { Bar, Line } from 'vue-chartjs';
import {
	Chart as ChartJS,
	Title,
	Tooltip,
	Legend,
	BarElement,
	CategoryScale,
	LinearScale,
	LineElement,
	PointElement,
	Filler
} from 'chart.js';
import {
	createKpiBarOptions,
	createKpiLineOptions,
	createConversionBarOptions,
	createBar3DPlugin
} from '@/includes/chartConfigs';
import { chartCsvFilename } from '@/includes/branding';

ChartJS.register(
	Title,
	Tooltip,
	Legend,
	BarElement,
	CategoryScale,
	LinearScale,
	LineElement,
	PointElement,
	Filler
);

const FULL_DATA_MAP = {
	total_revenue: 'paid',
	payments_made: 'paid',
	refunds: 'returned',
	conversion: ''
};

export default {
	components: {
		Icon,
		Popper,
		Bar,
		Line
	},
	props: {
		close: [Function]
	},
	data() {
		const content = this.$store.state.modal.content;
		return {
			content,
			loading: true,
			invoices: [],
			totalCount: 0,
			barOptions: null,
			lineOptions: null,
			conversionBarOptions: null,
			bar3DPlugin: null,
			chartColors: null
		};
	},
	computed: {
		isMobile() {
			return this.$store.state.resize.w <= 768;
		},
		isConversion() {
			return this.content.chartKey === 'conversion';
		}
	},
	methods: {
		modalDeltaClass(deltaClass) {
			const color = (deltaClass || '').split('--').pop();
			return color ? `chart-modal__delta--${color}` : '';
		},
		getDeltaArrow(delta) {
			const num = parseFloat(delta);
			if (isNaN(num) || num === 0) return '';
			return num > 0 ? '▲' : '▼';
		},
		getChartColors() {
			const style = getComputedStyle(this.$el);
			const get = (name) => style.getPropertyValue(name).trim();
			return {
				green: get('--chart-green') || '#22C55E',
				blue: get('--chart-blue') || '#3B82F6',
				blueLight: get('--chart-blue-light') || '#93C5FD',
				orange: get('--chart-orange') || '#F97316',
				violet: get('--chart-violet') || '#8B5CF6',
				orangeWarm: get('--chart-orange-warm') || '#FB923C',
				blueGrey: get('--chart-blue-grey') || '#64748B',
				brown: get('--chart-brown') || '#A16207',
				grey: get('--chart-grey') || '#9CA3AF',
				grid: get('--chart-grid') || 'rgba(156, 163, 175, 0.15)',
				placeholder: get('--chart-placeholder') || '#E5E7EB',
				menuBg: get('--chart-menu-bg') || '#122157'
			};
		},
		handleModalTooltip(context) {
			let tooltipEl = document.getElementById('chart-modal-tooltip');

			if (!tooltipEl) {
				tooltipEl = document.createElement('div');
				tooltipEl.id = 'chart-modal-tooltip';
				tooltipEl.innerHTML = '<div class="chart-modal-tooltip-content"></div>';
				document.body.appendChild(tooltipEl);
			}

			const tooltipModel = context.tooltip;
			if (tooltipModel.opacity === 0) {
				tooltipEl.style.opacity = 0;
				return;
			}

			if (tooltipModel.body) {
				const titleLines = tooltipModel.title || [];
				const bodyLines = tooltipModel.body.map(b => b.lines);

				let innerHtml = '';

				titleLines.forEach(function (title) {
					innerHtml += '<div class="tooltip-title neue">' + title + '</div>';
				});

				bodyLines.forEach(function (body) {
					const value = Array.isArray(body) ? body[0] : body;
					const numValue = typeof value === 'string' ? value.split(':').pop().trim() : value;
					innerHtml += '<div class="tooltip-body neue fw-bold">' + numValue + '</div>';
				});

				const contentEl = tooltipEl.querySelector('.chart-modal-tooltip-content');
				contentEl.innerHTML = innerHtml;
			}

			const position = context.chart.canvas.getBoundingClientRect();

			tooltipEl.style.opacity = 1;
			tooltipEl.style.position = 'absolute';
			tooltipEl.style.left = position.left + window.pageXOffset + tooltipModel.caretX + 'px';
			tooltipEl.style.top = position.top + window.pageYOffset + tooltipModel.caretY - tooltipEl.offsetHeight - 10 + 'px';
			tooltipEl.style.pointerEvents = 'none';
			tooltipEl.style.zIndex = '10001';
		},
		statusClass(status) {
			return this.$lib.getInvoiceStatusClass(status);
		},
		transformStatusText(status) {
			return this.$lib.getInvoiceStatusText(status);
		},
		formatInvDate(dateStr) {
			if (!dateStr) return '—';
			const parts = dateStr.split(' ');
			return parts[0] || dateStr;
		},
		formatAmount(amount) {
			if (amount === null || amount === undefined) return '—';
			const num = Number(amount);
			if (isNaN(num)) return String(amount);
			return num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
		},
		async fetchInvoices() {
			const fullData = FULL_DATA_MAP[this.content.chartKey];
			if (fullData === undefined) {
				this.loading = false;
				return;
			}

			const requestBody = {
				widgets: { counts_made: {} },
				filters: this.content.filters,
				full_data: fullData
			};

			try {
				const response = await this.axios.post('GetDashboardInvoices', { no_loader: true, data: requestBody });
				const data = response.data.body || response.data;
				const isSuccess = data.result === undefined || data.result === 0 || data.result === '0';

				if (isSuccess && data.full_invoices) {
					this.invoices = data.full_invoices.data || [];
					this.totalCount = data.full_invoices.meta?.total || this.invoices.length;
				}
			} catch (e) {
				console.error('Failed to fetch full invoices:', e);
			} finally {
				this.loading = false;
			}
		},
		downloadCSV() {
			const t = (key) => this.$t(`views.Merchant.DashboardInvoices.${key}`);
			const SEP = ';';
			const headers = [t('22'), t('23'), t('25'), t('26')];
			if (this.isConversion) headers.push(t('28'));

			const esc = (val) => {
				const str = String(val ?? '');
				return str.includes(SEP) || str.includes('"') || str.includes('\n')
					? `"${str.replace(/"/g, '""')}"` : str;
			};

			const rows = this.invoices.map((inv) => {
				const row = [
					inv.code,
					inv.dt_create || '',
					inv.shop_name || inv.shop,
					`${this.formatAmount(inv.amount)} ${inv.currency || ''}`.trim()
				];
				if (this.isConversion) row.push(this.transformStatusText(inv.status));
				return row.map(esc).join(SEP);
			});

			const csv = '\uFEFF' + [headers.map(esc).join(SEP), ...rows].join('\n');
			const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
			const url = window.URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = url;
			// For Graphite, derive only the safe filename from the immutable runtime
			// brand. CSV headers, rows and values above remain unchanged.
			const baseName = `${new Date().toISOString().split('T')[0]}_dashboard_${this.content.chartKey || 'invoices'}.csv`;
			link.download = chartCsvFilename(process.env.VUE_APP_INDIVIDUAL, baseName);
			link.style.display = 'none';
			document.body.appendChild(link);
			link.click();
			setTimeout(() => {
				document.body.removeChild(link);
				window.URL.revokeObjectURL(url);
			}, 100);
		}
	},
	mounted() {
		this.chartColors = this.getChartColors();
		this.barOptions = createKpiBarOptions(this.handleModalTooltip);
		this.lineOptions = createKpiLineOptions(this.handleModalTooltip);
		this.conversionBarOptions = createConversionBarOptions();
		this.bar3DPlugin = createBar3DPlugin(this.chartColors);
		this.fetchInvoices();
	}
};
</script>

<style lang="scss" scoped>
.chart-modal {
	--chart-green: #{$color-notification};
	--chart-blue: #{$color-blue};
	--chart-blue-light: #{$color-blue-light};
	--chart-orange: #{$dark-orange};
	--chart-violet: #{$color-violet};
	--chart-orange-warm: #{$color-orange};
	--chart-blue-grey: #{$color-blue-grey};
	--chart-brown: #{$color-brown};
	--chart-grey: #{$primary-grey};
	--chart-grid: #{rgba($primary-grey, 0.15)};
	--chart-placeholder: #{$color-lightgrey};
	--chart-menu-bg: #{$menu-bg};
}

.chart-modal__content {
	background: #ffffff;
}

.chart-modal__info {
	cursor: pointer;
	opacity: 0.6;
	transition: opacity 0.2s;

	&:hover {
		opacity: 1;
	}
}

.chart-modal__delta {
	font-size: 14px;
	line-height: 20px;
	padding: 2px 8px;
	border-radius: 999px;
	white-space: nowrap;
}

.chart-modal__delta--green {
	background: $green-status-light;
	color: $color-system-green;
}

.chart-modal__delta--blue {
	background: rgba($color-blue, 0.2);
	color: $color-blue;
}

.chart-modal__delta--orange {
	background: $orange-status-light;
	color: $dark-orange;
}

.chart-modal__arrow {
	margin-right: 4px;
	font-size: 10px;
}

.chart-modal__chart {
	height: 192px;
}

.chart-modal__loading,
.chart-modal__empty {
	height: 120px;
}

.chart-modal__tbl {
	width: 100%;
	table-layout: fixed;
	border-collapse: collapse;
	font-size: 12px;
	line-height: 16px;
	color: $primary-grey;

	th {
		background: $bg-light-blue;
		padding: 8px;
		font-weight: normal;
		white-space: nowrap;

		&:last-child {
			padding-right: 24px;
		}
	}

	td {
		padding: 10px;
		vertical-align: middle;
		border-bottom: 1px solid $primary-stroke;
		color: #000;
	}
}

.chart-modal__tbl-head {
	border-collapse: separate;
	border-spacing: 0;
	border-radius: 8px 8px 0 0;
	overflow: hidden;
}

.chart-modal__col-id {
	width: 64px;

	td & {
		color: $primary-grey;
	}
}

.chart-modal__table-scroll {
	max-height: 324px;
	overflow-y: auto;
	padding-right: 8px;

	&::-webkit-scrollbar {
		width: 4px;
	}

	&::-webkit-scrollbar-track {
		background: $menu-bg;
		background: color-mix(in srgb, #{$menu-bg} 20%, transparent);
		border-radius: 2px;
		margin-top: 8px;
		margin-bottom: 8px;
	}

	&::-webkit-scrollbar-thumb {
		background: $menu-bg;
		border-radius: 2px;
	}

	&::-webkit-scrollbar-button {
		display: none;
	}
}

.chart-modal__header--mobile {
	position: relative;
}

.chart-modal__back {
	position: absolute;
	left: 0;
}

.chart-modal__tbl--mobile {
	.chart-modal__col-id {
		width: 70px;
	}

	td {
		padding: 12px 10px;
	}
}

.chart-modal__col-id--top {
	vertical-align: top;
}

.chart-modal__col-status {
	vertical-align: middle;
	white-space: nowrap;
}

.chart-modal__download {
	border: 1px solid $primary-stroke;
	border-radius: 8px;
	padding: 10px 16px;
	background: #ffffff;
	color: $primary-grey;
	transition: opacity 0.2s;

	&:hover {
		opacity: 0.8;
	}
}
</style>

<style lang="scss">
#chart-modal-tooltip {
	opacity: 0;
	position: absolute;
	background: $menu-bg;
	color: white;
	border-radius: 6px;
	padding: 6px 10px;
	pointer-events: none;
	transform: translate(-50%, 0);
	transition: opacity 0.15s ease;
	z-index: 10001;

	.chart-modal-tooltip-content {
		text-align: center;

		.tooltip-title {
			font-size: 11px;
			color: rgba(255, 255, 255, 0.7);
			margin-bottom: 2px;
		}

		.tooltip-body {
			font-size: 13px;
			color: white;
		}
	}

	&::after {
		content: '';
		position: absolute;
		bottom: -5px;
		left: 50%;
		transform: translateX(-50%);
		width: 0;
		height: 0;
		border-left: 5px solid transparent;
		border-right: 5px solid transparent;
		border-top: 5px solid $menu-bg;
	}
}
</style>
